import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { IEventRegistrationSurvey } from '../../../../../extViewmodels';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../../models';
import { Button } from '../../../../components/Button';
import { ToggleAnimationWrapper } from '../../../../components/helpers/ToggleAnimationWrapper';
import { XIcon } from '../../../../components/svgs/icons/XIcon';
import { TextInput } from '../../../../components/TextInput';
import { TextInputFormField } from '../../../../components/TextInputFormField';
import { Toggle } from '../../../../components/Toggle';
import { grayIconFill } from '../../../../styles/colors';
import { baseStyleSheet, bs } from '../../../../styles/styles';
import { FormFieldWrapper } from '../../shared/components/FormFieldWrapper';
import { useNotificationOptions } from '../../shared/hooks/useNotificationOptions';
import { styleSheet } from '../styles';

export const NotificationOptionsBase: React.FC<
	IImpersonationContextComponentProps & { survey: IEventRegistrationSurvey }
> = ({ survey }) => {
	const formContext = useFormContext<IEventRegistrationSurvey>();

	const {
		recipients,
		isNotificationEnabled,
		hasAdditionalRecipients,
		shouldShowCreatorEmail,
		addRecipient,
		removeRecipient,
		updateRecipient,
	} = useNotificationOptions({
		formContext: formContext as any,
		isArchived: !!survey?.archivedDate,
		creatorEmail: survey?.creator?.email,
	});

	return (
		<section className={css(styleSheet.notificationSection)}>
			<div className={css(styleSheet.notificationSettingsContainer)}>
				<FormFieldWrapper
					name='notificationSettings.isEnabled'
					control={formContext.control}
					title='Email Notifications'
					description='Receive an email notification for each survey response.'
				>
					{({ onChange }) => (
						<Toggle
							id={'edit-survey-notification-enabled-toggle'}
							isOn={isNotificationEnabled}
							onToggleCheckChanged={onChange}
							uncheckedColor={grayIconFill}
						/>
					)}
				</FormFieldWrapper>
			</div>

			<ToggleAnimationWrapper isVisible={isNotificationEnabled} duration={300}>
				<>
					{shouldShowCreatorEmail && (
						<div className={css(styleSheet.formControl)}>
							<label htmlFor='edit-survey-notification-email'>Survey Creator</label>
							<TextInput
								type='text'
								inputId='edit-survey-notification-email'
								value={survey?.creator?.email}
								className={css(styleSheet.notificationEmail)}
								disabled
							/>
						</div>
					)}

					<fieldset
						className={css(
							styleSheet.fieldset,
							baseStyleSheet.verticalStack,
							!isNotificationEnabled ? styleSheet.disabled : null
						)}
						disabled={!isNotificationEnabled || !!survey?.archivedDate}
					>
						{isNotificationEnabled && hasAdditionalRecipients && (
							<label htmlFor='edit-survey-notification-email' className={css(styleSheet.additionalRecipientsLabel)}>
								Additional Recipients
							</label>
						)}
						<Controller
							name='notificationSettings.additionalRecipients'
							control={formContext.control}
							render={({ field: { ref, ..._fieldProps } }) => {
								return (
									<>
										{recipients.map((_recipient: string, index: number) => {
											const value = recipients[index];
											return (
												<div
													key={`edit-survey-notification-additional-recipient-${index}`}
													className={css(styleSheet.additionalRecipient)}
												>
													<div className={css(bs.flex, bs.gap2, bs.relative, bs.fullWidth, bs.itemsCenter)}>
														<div className={css(styleSheet.notificationRecipientInput)}>
															<TextInputFormField
																type={'text'}
																id={`edit-survey-notification-additional-recipient-${index}`}
																value={value}
																onChange={e => {
																	updateRecipient(index, e.target.value);
																}}
															/>
														</div>
														<div>
															<Button
																type='button'
																kind='custom'
																className={css(styleSheet.removeButton)}
																onClick={() => {
																	removeRecipient(index);
																}}
															>
																<span>Remove</span>
																<XIcon className={css(styleSheet.removeButtonIcon)} />
															</Button>
														</div>
														<p className={css(styleSheet.errorMessage, styleSheet.noMargin)}>
															{formContext.formState.errors?.notificationSettings?.additionalRecipients?.message}
														</p>
													</div>
												</div>
											);
										})}
										<Button type='button' kind='link' onClick={addRecipient}>
											+ Add Email
										</Button>
									</>
								);
							}}
						/>
					</fieldset>
				</>
			</ToggleAnimationWrapper>
		</section>
	);
};

export const NotificationOptions = inject(ImpersonationContextKey)(NotificationOptionsBase);
